import { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import Toast from '../components/Toast';

function Account() {
  const { user, token, isAuthenticated, authLoading, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  const [saveChatHistory, setSaveChatHistory] = useState(true);
  const [toggleSaving, setToggleSaving] = useState(false);

  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (isAuthenticated === false) navigate('/login');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setSaveChatHistory(user.settings?.saveChatHistory ?? false);
    }
  }, [user]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const handleSaveName = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setProfileSaving(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: name.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update name');
      await refreshUser();
      showToast('Name updated successfully');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      return showToast('New passwords do not match', 'error');
    }
    if (newPassword.length < 8) {
      return showToast('New password must be at least 8 characters', 'error');
    }
    setPasswordSaving(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/password`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update password');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showToast('Password updated successfully');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleToggleChatHistory = async (value) => {
    setSaveChatHistory(value);
    setToggleSaving(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/user/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ saveChatHistory: value }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update setting');
      await refreshUser();
      showToast(`Chat history saving ${value ? 'enabled' : 'disabled'}`);
    } catch (err) {
      setSaveChatHistory(!value);
      showToast(err.message, 'error');
    } finally {
      setToggleSaving(false);
    }
  };

  if (authLoading || isAuthenticated === null) {
    return (
      <div className="flex justify-center items-center h-screen bg-white dark:bg-gray-900">
        <div className="w-80">
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-black rounded-full" style={{ width: '85%' }} />
          </div>
        </div>
      </div>
    );
  }

  if (!user) return null;

  const inputClass =
    'mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm shadow-sm focus:border-black dark:focus:border-white focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white';
  const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300';
  const btnPrimary =
    'inline-flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
  const sectionClass =
    'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-8 pb-16 px-4 sm:px-6 lg:px-8">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-heading font-semibold text-gray-900 dark:text-white mb-8">
          Account Settings
        </h1>

        {/* Profile */}
        <div className={sectionClass}>
          <h2 className="text-lg font-heading font-medium text-gray-900 dark:text-white mb-4">
            Profile
          </h2>
          <form onSubmit={handleSaveName} className="space-y-4">
            <div>
              <label htmlFor="name" className={labelClass}>Display name</label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputClass}
                required
              />
            </div>
            <div>
              <label className={labelClass}>Email</label>
              <input
                type="email"
                value={user.email}
                disabled
                className={`${inputClass} opacity-60 cursor-not-allowed`}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Email changes coming soon.
              </p>
            </div>
            <div className="flex justify-end">
              <button type="submit" disabled={profileSaving || !name.trim()} className={btnPrimary}>
                {profileSaving ? 'Saving…' : 'Save name'}
              </button>
            </div>
          </form>
        </div>

        {/* Password — hidden for Google OAuth users */}
        {!user.googleId && (
          <div className={sectionClass}>
            <h2 className="text-lg font-heading font-medium text-gray-900 dark:text-white mb-4">
              Password
            </h2>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label htmlFor="currentPassword" className={labelClass}>Current password</label>
                <input
                  id="currentPassword"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className={inputClass}
                  autoComplete="current-password"
                  required
                />
              </div>
              <div>
                <label htmlFor="newPassword" className={labelClass}>New password</label>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={inputClass}
                  autoComplete="new-password"
                  required
                  minLength={8}
                />
              </div>
              <div>
                <label htmlFor="confirmPassword" className={labelClass}>Confirm new password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={inputClass}
                  autoComplete="new-password"
                  required
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={passwordSaving || !currentPassword || !newPassword || !confirmPassword}
                  className={btnPrimary}
                >
                  {passwordSaving ? 'Saving…' : 'Update password'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Privacy & Data */}
        <div className={sectionClass}>
          <h2 className="text-lg font-heading font-medium text-gray-900 dark:text-white mb-1">
            Privacy &amp; Data
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Control how Oberon handles your conversation data.
          </p>

          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Save chat history
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                When enabled, Oberon saves your conversations so you can resume previous sessions.
                Disabling this means each session starts fresh and nothing is stored.
                The current status is also shown in the Oberon panel.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={saveChatHistory}
              disabled={toggleSaving}
              onClick={() => handleToggleChatHistory(!saveChatHistory)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                saveChatHistory ? 'bg-black dark:bg-white' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white dark:bg-gray-900 shadow ring-0 transition duration-200 ease-in-out ${
                  saveChatHistory ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <p className={`mt-3 text-xs font-medium ${saveChatHistory ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
            Chat history saving is currently {saveChatHistory ? 'enabled' : 'disabled'}
          </p>
        </div>
      </div>
    </div>
  );
}

export default Account;
